import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  signInSchema,
  signUpFormSchema,
  type SignInDto,
  type SignInFormValues,
  type SignUpFormDto,
  type SignUpFormValues,
} from "shared/dto/auth";
import { DEFAULT_LOCALE, isSupportedLocale, type SupportedLocale } from "@app/i18n";
import { cn } from "@app/ui/lib/Utils";
import { Button } from "@app/ui/components/Button";
import {
  resolveSocialProviders,
  type SocialProvider,
  type SocialProviderId,
} from "@app/ui/features/auth/socialProviders";
import { sortSocialProviders } from "@app/ui/features/auth/socialProviderSort";
import { EmailField } from "@app/ui/features/auth/EmailField";
import { NicknameField } from "@app/ui/features/auth/NicknameField";
import { PasswordField } from "@app/ui/features/auth/PasswordField";
import { PasswordConfirmField } from "@app/ui/features/auth/PasswordConfirmField";
import { useAuthErrorText } from "@app/ui/hooks/UseAuthErrorText";
import { Logo } from "@app/ui/components/Logo";
import { AuthLocaleSelect } from "@app/ui/components/AuthLocaleSelect";

export interface LoginViewProps {
  /** "login": 이메일 + 비밀번호로 로그인. "register": 닉네임 + 이메일 + 비밀번호로 신규 계정 생성. */
  mode: "login" | "register";
  onSubmit: (values: { email: string; password: string }) => void;
  onSignUp: (values: { email: string; nickname: string; password: string }) => void;
  /** 주어지면 로그인 ↔ 가입 화면을 전환하는 버튼을 노출한다. */
  onSwitchMode?: () => void;
  /** 주어지면(로그인 모드 한정) "비밀번호를 잊으셨나요?" 링크를 노출한다. */
  onForgotPassword?: () => void;
  /** 서버가 실제로 활성화한 소셜 프로바이더 id 목록(/api/auth-providers). 비었거나
   * onSocialLogin 이 없으면 소셜 영역 자체를 렌더하지 않는다(순수 이메일 폼). */
  socialProviders?: string[];
  /** 직전에 사용한 로그인 방법(localStorage). 소셜 프로바이더면 "최근 사용" 뱃지와 함께
   * 맨 위로 끌어올려 강조한다("email" 이면 폼이 어차피 보이므로 무시). */
  lastAuthMethod?: string | null;
  /** 소셜 버튼 클릭 → 해당 provider 로 OAuth 시작. */
  onSocialLogin?: (provider: SocialProviderId) => void;
  /** 현재 UI 언어 ("ko" | "en"). onLocaleChange 와 함께 주면 언어 드롭다운을 노출한다.
   * 소셜 버튼의 언어별 우선순위(primaryFor) 판정에도 쓰인다. */
  locale?: string;
  /** 브라우저 언어 감지가 사용자 기대와 어긋났을 때 로그인 전에도 언어를 바꿀 수 있게 한다. */
  onLocaleChange?: (locale: string) => void;
  isSubmitting?: boolean;
  className?: string;
}

// 모듈 로드 시점에 문자열로 굳히면 언어 전환에 반응하지 못하므로 msg 디스크립터로
// 두고 렌더 시점에 i18n._() 로 해석한다.
const COPY: Record<
  LoginViewProps["mode"],
  { title: MessageDescriptor; submitLabel: MessageDescriptor }
> = {
  login: { title: msg`로그인`, submitLabel: msg`로그인` },
  // 가입은 더 이상 곧바로 로그인시키지 않는다 — 메일로 받은 인증 코드를 넣어야 끝난다.
  register: { title: msg`가입하기`, submitLabel: msg`인증 코드 받기` },
};

interface AuthFormProps {
  isSubmitting?: boolean;
  submitLabel: string;
  /** 제출 버튼 아래에 붙는 공통 버튼들(비밀번호 찾기 / 모드 전환). */
  footer?: React.ReactNode;
}

/** 로그인 폼. 검증 규칙은 서버와 공유하는 shared/dto/auth 의 signInSchema 하나뿐이다. */
function SignInForm({
  onSubmit,
  isSubmitting,
  submitLabel,
  footer,
}: AuthFormProps & { onSubmit: (values: SignInDto) => void }) {
  const { t } = useLingui();
  const errorText = useAuthErrorText();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInFormValues, unknown, SignInDto>({
    // 제네릭을 명시하지 않으면 resolver 의 Output 이 unknown 으로 추론돼
    // handleSubmit 이 받는 값 타입이 깨진다.
    resolver: valibotResolver<SignInFormValues, unknown, SignInDto>(signInSchema),
    defaultValues: { email: "", password: "" },
    mode: "onTouched",
  });

  return (
    // noValidate: 브라우저 기본 검증이 먼저 끼어들면 valibot 메시지 대신 브라우저
    // 언어의 말풍선이 뜬다. 검증은 전적으로 스키마에 맡긴다.
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-3">
      <Controller
        control={control}
        name="email"
        render={({ field }) => (
          <EmailField
            ref={field.ref}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            error={errorText(errors.email?.message)}
            disabled={isSubmitting}
          />
        )}
      />
      <Controller
        control={control}
        name="password"
        render={({ field }) => (
          <PasswordField
            ref={field.ref}
            id="login-password"
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            error={errorText(errors.password?.message)}
            disabled={isSubmitting}
          />
        )}
      />
      <Button type="submit" className="mt-2 w-full" disabled={isSubmitting}>
        {isSubmitting ? t`처리 중...` : submitLabel}
      </Button>
      {footer}
    </form>
  );
}

/** 가입 폼. 비밀번호 확인 대조까지 signUpFormSchema 가 담당한다. */
function SignUpForm({
  onSubmit,
  isSubmitting,
  submitLabel,
  footer,
}: AuthFormProps & { onSubmit: (values: SignUpFormDto) => void }) {
  const { t } = useLingui();
  const errorText = useAuthErrorText();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpFormValues, unknown, SignUpFormDto>({
    resolver: valibotResolver<SignUpFormValues, unknown, SignUpFormDto>(signUpFormSchema),
    defaultValues: { email: "", nickname: "", password: "", passwordConfirm: "" },
    mode: "onTouched",
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-3">
      <Controller
        control={control}
        name="nickname"
        render={({ field }) => (
          <NicknameField
            ref={field.ref}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            error={errorText(errors.nickname?.message)}
            disabled={isSubmitting}
          />
        )}
      />
      <Controller
        control={control}
        name="email"
        render={({ field }) => (
          <EmailField
            ref={field.ref}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            error={errorText(errors.email?.message)}
            disabled={isSubmitting}
          />
        )}
      />
      <Controller
        control={control}
        name="password"
        render={({ field }) => (
          <PasswordField
            ref={field.ref}
            id="login-password"
            autoComplete="new-password"
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            error={errorText(errors.password?.message)}
            disabled={isSubmitting}
          />
        )}
      />
      <Controller
        control={control}
        name="passwordConfirm"
        render={({ field }) => (
          <PasswordConfirmField
            ref={field.ref}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            error={errorText(errors.passwordConfirm?.message)}
            disabled={isSubmitting}
          />
        )}
      />
      <Button type="submit" className="mt-2 w-full" disabled={isSubmitting}>
        {isSubmitting ? t`처리 중...` : submitLabel}
      </Button>
      {footer}
    </form>
  );
}

/** 소셜 로그인 버튼 하나. 아이콘 + "{Provider}로 계속하기" 라벨. */
function SocialButton({
  provider,
  disabled,
  onClick,
}: {
  provider: SocialProvider;
  disabled?: boolean;
  onClick: () => void;
}) {
  const { i18n } = useLingui();
  const { Icon } = provider;
  return (
    <Button
      type="button"
      variant="outline"
      className="w-full"
      disabled={disabled}
      onClick={onClick}
    >
      <Icon />
      {i18n._(provider.brand)}
    </Button>
  );
}

/**
 * 소셜 로그인 영역. 서버가 켠 프로바이더를 "최근 사용 → 언어 우선 → 더 보기" 순으로
 * 배치하고, 아래에 "또는" 구분선을 둔다(이메일 폼과의 경계). 프로바이더가 하나뿐이면
 * primary 에 그것만 놓여 자연스럽게 단일 버튼으로 축약된다.
 */
function SocialAuthSection({
  providers,
  locale,
  lastAuthMethod,
  onSocialLogin,
  disabled,
}: {
  providers: SocialProvider[];
  locale: SupportedLocale;
  lastAuthMethod?: string | null;
  onSocialLogin: (provider: SocialProviderId) => void;
  disabled?: boolean;
}) {
  const [showMore, setShowMore] = React.useState(false);
  const { recent, primary, secondary } = sortSocialProviders(
    providers,
    locale,
    lastAuthMethod ?? null,
  );

  return (
    <div className="mb-4 flex flex-col gap-2">
      {recent && (
        <div className="flex flex-col gap-1.5">
          <span className="px-1 text-xs font-medium text-muted-foreground">
            <Trans>최근 사용</Trans>
          </span>
          <SocialButton provider={recent} disabled={disabled} onClick={() => onSocialLogin(recent.id)} />
        </div>
      )}

      {primary.map((provider) => (
        <SocialButton
          key={provider.id}
          provider={provider}
          disabled={disabled}
          onClick={() => onSocialLogin(provider.id)}
        />
      ))}

      {secondary.length > 0 &&
        (showMore ? (
          secondary.map((provider) => (
            <SocialButton
              key={provider.id}
              provider={provider}
              disabled={disabled}
              onClick={() => onSocialLogin(provider.id)}
            />
          ))
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground"
            disabled={disabled}
            onClick={() => setShowMore(true)}
          >
            <Trans>다른 방법으로 로그인</Trans>
          </Button>
        ))}

      <div className="relative my-1">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-background px-2 text-muted-foreground">
            <Trans>또는</Trans>
          </span>
        </div>
      </div>
    </div>
  );
}

const LoginView = React.forwardRef<HTMLDivElement, LoginViewProps>(
  (
    {
      mode,
      onSubmit,
      onSignUp,
      onSwitchMode,
      onForgotPassword,
      socialProviders,
      lastAuthMethod,
      onSocialLogin,
      locale,
      onLocaleChange,
      isSubmitting,
      className,
    },
    ref
  ) => {
    const { t, i18n } = useLingui();
    const isRegister = mode === "register";
    const { title, submitLabel } = COPY[mode];

    // 서버가 켠 프로바이더를 레지스트리 메타로 해석한다. 하나라도 있고 핸들러가 주어졌을
    // 때만 소셜 영역을 렌더한다(그 외엔 순수 이메일 폼 — 기존 동작 유지).
    const resolvedProviders = resolveSocialProviders(socialProviders ?? []);
    const hasSocial = resolvedProviders.length > 0 && Boolean(onSocialLogin);
    // 정렬의 언어 축은 지원 언어로 좁힌다(미지의 locale prop 은 기본값으로).
    const activeLocale: SupportedLocale = isSupportedLocale(locale) ? locale : DEFAULT_LOCALE;

    const footer = (
      <>
        {!isRegister && onForgotPassword && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="w-full text-muted-foreground"
            disabled={isSubmitting}
            onClick={onForgotPassword}
          >
            <Trans>비밀번호를 잊으셨나요?</Trans>
          </Button>
        )}

        {onSwitchMode && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground"
            disabled={isSubmitting}
            onClick={onSwitchMode}
          >
            {isRegister ? t`로그인으로 돌아가기` : t`가입하기`}
          </Button>
        )}
      </>
    );

    return (
      <div
        ref={ref}
        className={cn(
          "relative flex min-h-screen items-center justify-center bg-background p-6",
          className
        )}
      >
        <AuthLocaleSelect locale={locale} onLocaleChange={onLocaleChange} />
        <div className="w-full max-w-xs">
          <div className="mb-8 flex justify-center">
            <Logo size="lg" />
          </div>
          <h1 className="mb-6 text-center text-base font-semibold">{i18n._(title)}</h1>

          {hasSocial && (
            <SocialAuthSection
              providers={resolvedProviders}
              locale={activeLocale}
              lastAuthMethod={lastAuthMethod}
              onSocialLogin={onSocialLogin!}
              disabled={isSubmitting}
            />
          )}

          {isRegister ? (
            // 모드가 바뀌면 폼 인스턴스를 새로 만든다(key) — 로그인에서 치던 값과
            // 검증 에러가 가입 폼으로 넘어오지 않게.
            <SignUpForm
              key="register"
              onSubmit={({ email, nickname, password }) => onSignUp({ email, nickname, password })}
              isSubmitting={isSubmitting}
              submitLabel={i18n._(submitLabel)}
              footer={footer}
            />
          ) : (
            <SignInForm
              key="login"
              onSubmit={onSubmit}
              isSubmitting={isSubmitting}
              submitLabel={i18n._(submitLabel)}
              footer={footer}
            />
          )}
        </div>
      </div>
    );
  }
);
LoginView.displayName = "LoginView";

export { LoginView };
